import React from 'react';
import { CheckCircle, Plus } from 'lucide-react';

// Format text to bold content before colons for better scannability
const formatTextWithColons = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Find content before the first colon and make it bold
  const colonIndex = text.indexOf(':');
  if (colonIndex > 0) {
    const beforeColon = text.substring(0, colonIndex).trim();
    const afterColon = text.substring(colonIndex).trim();
    return `<strong>${beforeColon}</strong>${afterColon}`;
  }
  
  return text;
};

const CollegeRecommendationsSection = ({ 
  recommendations, 
  integrationStatus, 
  addCollegeToList, 
  isIntegrating 
}) => {
  if (!recommendations) return null;

  const SchoolList = ({ title, schools, bgColor, borderColor, icon, category }) => (
    <div className={`${bgColor} border ${borderColor} rounded-2xl p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
        </div>
        {addCollegeToList && (
          <button
            onClick={() => addAllColleges(schools, category)}
            disabled={isIntegrating || areAllAdded(schools, category)}
            className={`text-xs px-3 py-1 rounded-lg font-medium transition-colors ${
              areAllAdded(schools, category)
                ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            {areAllAdded(schools, category) ? '✓ All Added' : 'Add All'}
          </button>
        )}
      </div>
      <ul className="space-y-3">
        {schools?.map((school, index) => {
          const collegeName = parseCollegeName(school);
          const collegeKey = `${category}-${collegeName}`;
          const isAdded = integrationStatus?.colleges?.[collegeKey]?.added;
          
          return (
            <li key={index} className="text-slate-700 text-sm leading-relaxed">
              <div className="flex items-start gap-2">
                <span className="text-teal-500 font-bold mt-1">•</span>
                <div className="flex-1">
                  {typeof school === 'object' && school.school ? (
                    // New object format with detailed info
                    <div>
                      {/* School name and Add button on same line */}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold text-slate-900">{school.school}</div>
                        {addCollegeToList && (
                          <button
                            onClick={() => addCollegeToList(school, category)}
                            disabled={isIntegrating || isAdded}
                            className={`flex-shrink-0 text-xs px-2 py-1 rounded font-medium transition-colors flex items-center gap-1 ${
                              isAdded
                                ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                                : 'bg-azure-100 hover:bg-azure-200 text-azure-700'
                            }`}
                          >
                            {isIntegrating ? (
                              <div className="w-3 h-3 border border-azure-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : isAdded ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Added
                              </>
                            ) : (
                              <>
                                <Plus className="w-3 h-3" />
                                Add
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      {/* Description spans full width */}
                      {school.whyYouMatch && (
                        <div className="text-slate-600 text-sm mt-1">
                          <strong>Why you match:</strong> {school.whyYouMatch}
                        </div>
                      )}
                      {school.competitiveEdge && (
                        <div className="text-teal-700 text-sm mt-1">
                          <strong>Your edge:</strong> {school.competitiveEdge}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Legacy string format
                    <div>
                      {/* For legacy format, we need to separate school name from description */}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold text-slate-900">
                          {(() => {
                            const colonIndex = school.indexOf(':');
                            return colonIndex > 0 ? school.substring(0, colonIndex).trim() : school;
                          })()}
                        </div>
                        {addCollegeToList && (
                          <button
                            onClick={() => addCollegeToList(school, category)}
                            disabled={isIntegrating || isAdded}
                            className={`flex-shrink-0 text-xs px-2 py-1 rounded font-medium transition-colors flex items-center gap-1 ${
                              isAdded
                                ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                                : 'bg-azure-100 hover:bg-azure-200 text-azure-700'
                            }`}
                          >
                            {isIntegrating ? (
                              <div className="w-3 h-3 border border-azure-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : isAdded ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Added
                              </>
                            ) : (
                              <>
                                <Plus className="w-3 h-3" />
                                Add
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      {/* Description spans full width */}
                      {(() => {
                        const colonIndex = school.indexOf(':');
                        if (colonIndex > 0) {
                          const afterColon = school.substring(colonIndex + 1).trim();
                          return (
                            <div className="text-slate-600 text-sm" 
                                 dangerouslySetInnerHTML={{ __html: formatTextWithColons(afterColon) }} />
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const parseCollegeName = (school) => {
    // Handle both object format and string format
    if (typeof school === 'object' && school.school) {
      return school.school.trim();
    }
    if (typeof school === 'string') {
      const colonIndex = school.indexOf(':');
      return colonIndex > 0 ? school.substring(0, colonIndex).trim() : school.trim();
    }
    return String(school).trim();
  };

  const areAllAdded = (schools, category) => {
    return schools?.every(school => {
      const collegeName = parseCollegeName(school);
      const collegeKey = `${category}-${collegeName}`;
      return integrationStatus?.colleges?.[collegeKey]?.added;
    });
  };

  const addAllColleges = async (schools, category) => {
    if (!schools || isIntegrating || !addCollegeToList) return;
    
    for (const school of schools) {
      const collegeName = parseCollegeName(school);
      const collegeKey = `${category}-${collegeName}`;
      
      if (!integrationStatus?.colleges?.[collegeKey]?.added) {
        await addCollegeToList(school, category);
        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };

  return (
    <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-10 shadow-xl relative overflow-hidden mb-12">
      {/* Top gradient bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-azure-400 via-violet-500 to-emerald-500"></div>
      
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">
          {recommendations.title || "College Recommendations"}
        </h2>
        <p className="text-stone-500 text-lg">
          {recommendations.subtitle || "Schools that would be perfect for someone like you"}
        </p>
      </div>

      {/* Schools Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <SchoolList 
          title="Reach Schools"
          schools={recommendations.reach}
          bgColor="bg-red-50"
          borderColor="border-red-200"
          icon="🚀"
          category="reach"
        />
        
        <SchoolList 
          title="Target Schools"
          schools={recommendations.target}
          bgColor="bg-yellow-50"
          borderColor="border-yellow-200"
          icon="🎯"
          category="target"
        />
        
        <SchoolList 
          title="Safety Schools"
          schools={recommendations.safety}
          bgColor="bg-emerald-50"
          borderColor="border-emerald-200"
          icon="✅"
          category="safety"
        />
      </div>
    </div>
  );
};

export default CollegeRecommendationsSection;